import { Track } from 'livekit-client';
import * as React from 'react';
import {
  MediaDeviceMenu,
  DisconnectButton,
  TrackToggle,
  ChatIcon,
  GearIcon,
  LeaveIcon,
  useLocalParticipantPermissions,
  usePersistentUserChoices,
  useMaybeLayoutContext,
  StartMediaButton,
} from '@livekit/components-react';
import { supportsScreenSharing } from '@livekit/components-core';
import clsx from 'clsx';

// Simple useMediaQuery hook (similar to the one in @livekit/components-react)
function useMediaQuery(query: string): boolean {
  const getMatches = (query: string): boolean => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = React.useState<boolean>(getMatches(query));

  React.useEffect(() => {
    const matchMedia = window.matchMedia(query);
    handleChange();
    function handleChange() {
      setMatches(getMatches(query));
    }
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener('change', handleChange);
    }
    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener('change', handleChange);
      }
    };
  }, [query]);

  return matches;
}

// Simple mergeProps utility (similar to the one in @livekit/components-react)
function mergeProps<T extends React.HTMLAttributes<HTMLElement>>(
  ...props: (T | undefined)[]
): T {
  const result = { ...props[0] } as T;
  for (let i = 1; i < props.length; i++) {
    const prop = props[i];
    if (!prop) continue;
    for (const key in prop) {
      const a = result[key];
      const b = prop[key];
      // Chain onClick handlers
      if (key === 'onClick' && typeof a === 'function' && typeof b === 'function') {
        result[key] = ((e: React.MouseEvent<HTMLElement>) => {
          a(e);
          b(e);
        }) as any;
      } else if (
        (key === 'className' || key === 'class') &&
        typeof a === 'string' &&
        typeof b === 'string'
      ) {
        result[key] = clsx(a, b) as any;
      } else {
        result[key] = (b !== undefined ? b : a) as any;
      }
    }
  }
  return result;
}

// Simple SettingsMenuToggle component
const SettingsMenuToggle = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(function SettingsMenuToggle(props, ref) {
  const layoutContext = useMaybeLayoutContext();
  const { dispatch, state } = layoutContext?.widget || { dispatch: undefined, state: undefined };
  const className = 'lk-button lk-settings-toggle';

  const mergedProps = React.useMemo(() => {
    return mergeProps(
      props,
      {
        className,
        onClick: () => {
          if (dispatch) dispatch({ msg: 'toggle_settings' });
        },
        'aria-pressed': state?.showSettings ? 'true' : 'false',
      }
    );
  }, [props, className, dispatch, state]);

  return <button ref={ref} {...mergedProps} />;
});

/** @public */
export type ControlBarControls = {
  microphone?: boolean;
  camera?: boolean;
  chat?: boolean;
  screenShare?: boolean;
  leave?: boolean;
  settings?: boolean;
  participants?: boolean;
  raiseHand?: boolean;
};

/** @public */
export interface ControlBarProps extends React.HTMLAttributes<HTMLDivElement> {
  onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
  variation?: 'minimal' | 'verbose' | 'textOnly';
  controls?: ControlBarControls;
  /**
   * If `true`, the user's device choices will be persisted.
   * This will enable the user to have the same device choices when they rejoin the room.
   * @defaultValue true
   * @alpha
   */
  saveUserChoices?: boolean;
  /**
   * Callback function to toggle the participants drawer visibility.
   */
  onParticipantsToggle?: () => void;
  /**
   * Whether the participants drawer is currently open.
   */
  isParticipantsOpen?: boolean;
  /**
   * Callback function to toggle the auth drawer visibility.
   */
  onAuthDrawerToggle?: () => void;
  /**
   * Whether the auth drawer is currently open.
   */
  isAuthDrawerOpen?: boolean;
  /**
   * Whether the microphone control is disabled (e.g., by server-side mute).
   */
  microphoneDisabled?: boolean;
  /**
   * Reason why the microphone is disabled.
   */
  microphoneDisabledReason?: string;
  /**
   * Callback when microphone toggle is attempted. Return false to prevent the action.
   */
  onMicrophoneToggle?: (enabled: boolean) => boolean;
  /**
   * Whether the camera control is disabled (e.g., by server-side mute).
   */
  cameraDisabled?: boolean;
  /**
   * Reason why the camera is disabled.
   */
  cameraDisabledReason?: string;
  /**
   * Callback when camera toggle is attempted. Return false to prevent the action.
   */
  onCameraToggle?: (enabled: boolean) => boolean;
  /**
   * Whether screen sharing is disabled (read-only permission, or an anonymous
   * joiner who has not been granted it).
   */
  screenShareDisabled?: boolean;
  /**
   * Reason why screen sharing is disabled.
   */
  screenShareDisabledReason?: string;
  /**
   * Whether the local participant currently has their hand raised.
   */
  isHandRaised?: boolean;
  /**
   * Toggle the local participant's raised hand.
   */
  onRaiseHandToggle?: () => void;
  /**
   * Number of raised hands, badged on the Participants button so a moderator
   * with the drawer closed still notices a pending request.
   */
  raisedHandCount?: number;
  /**
   * Number of people in the waiting room, badged on the Participants button so
   * a moderator doesn't have to keep the drawer open to notice an arrival.
   */
  waitingCount?: number;
}

/**
 * The `ControlBar` prefab gives the user the basic user interface to control their
 * media devices (camera, microphone and screen share), open the `Chat` and leave the room.
 *
 * @remarks
 * This component is build with other LiveKit components like `TrackToggle`,
 * `DeviceSelectorButton`, `DisconnectButton` and `StartAudio`.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <ControlBar />
 * </LiveKitRoom>
 * ```
 * @public
 */
export function ControlBar({
  variation,
  controls,
  saveUserChoices = true,
  onDeviceError,
  onParticipantsToggle,
  isParticipantsOpen = false,
  onAuthDrawerToggle,
  isAuthDrawerOpen = false,
  microphoneDisabled = false,
  microphoneDisabledReason,
  onMicrophoneToggle,
  cameraDisabled = false,
  cameraDisabledReason,
  onCameraToggle,
  screenShareDisabled = false,
  screenShareDisabledReason,
  isHandRaised = false,
  onRaiseHandToggle,
  raisedHandCount = 0,
  waitingCount = 0,
  ...props
}: ControlBarProps) {
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const layoutContext = useMaybeLayoutContext();
  React.useEffect(() => {
    if (layoutContext?.widget.state?.showChat !== undefined) {
      setIsChatOpen(layoutContext?.widget.state?.showChat);
    }
  }, [layoutContext?.widget.state?.showChat]);
  const isTooLittleSpace = useMediaQuery(`(max-width: ${isChatOpen ? 1000 : 760}px)`);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const defaultVariation = isTooLittleSpace ? 'minimal' : 'verbose';
  variation ??= defaultVariation;

  const visibleControls = { leave: true, ...controls };

  const localPermissions = useLocalParticipantPermissions();

  if (!localPermissions) {
    visibleControls.camera = false;
    visibleControls.chat = false;
    visibleControls.microphone = false;
    visibleControls.screenShare = false;
    visibleControls.raiseHand = false;
  } else {
    visibleControls.camera ??= localPermissions.canPublish;
    visibleControls.microphone ??= localPermissions.canPublish;
    visibleControls.screenShare ??= localPermissions.canPublish;
    visibleControls.chat ??= localPermissions.canPublishData && controls?.chat;
    // canPublishData, NOT canPublish. The point of the original gate was to hide
    // this from viewers, who can't be granted a mic without a new token and so
    // would be asking for something no moderator can give. But read-only
    // members and admitted guests also have canPublish: false, and for them the
    // opposite is true — raising a hand is the ONLY way they can ask, and a
    // moderator can grant it without a rejoin. canPublishData separates the two
    // exactly: viewers don't have it, restricted participants do.
    visibleControls.raiseHand ??= localPermissions.canPublishData && controls?.raiseHand;
  }

  const showIcon = React.useMemo(
    () => variation === 'minimal' || variation === 'verbose',
    [variation],
  );
  const showText = React.useMemo(
    () => variation === 'textOnly' || variation === 'verbose',
    [variation],
  );

  const browserSupportsScreenSharing = supportsScreenSharing();

  const [isScreenShareEnabled, setIsScreenShareEnabled] = React.useState(false);

  const onScreenShareChange = React.useCallback(
    (enabled: boolean) => {
      setIsScreenShareEnabled(enabled);
    },
    [setIsScreenShareEnabled],
  );

  // The caller supplies the copy because only it knows WHY the control is
  // locked; these fallbacks keep the previous wording for callers that don't.
  const micLockText =
    microphoneDisabledReason || 'Microphone is disabled. A moderator has muted you.';
  const cameraLockText =
    cameraDisabledReason || 'Camera is disabled. A moderator has muted you.';

  const htmlProps = mergeProps({ className: 'lk-control-bar' }, props);

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: !saveUserChoices });

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) => {
      // Prevent unmute if microphone is disabled by server
      if (!enabled && microphoneDisabled && isUserInitiated) {
        console.warn('Cannot unmute microphone: disabled by server');
        return; // Prevent the action
      }
      
      // Check with parent component if action should be allowed
      if (isUserInitiated && onMicrophoneToggle) {
        const shouldAllow = onMicrophoneToggle(enabled);
        if (!shouldAllow) {
          return; // Prevent the action
        }
      }
      
      return isUserInitiated ? saveAudioInputEnabled(enabled) : null;
    },
    [saveAudioInputEnabled, microphoneDisabled, onMicrophoneToggle],
  );

  const cameraOnChange = React.useCallback(
    (enabled: boolean, isUserInitiated: boolean) => {
      // Prevent unmute if camera is disabled by server
      if (!enabled && cameraDisabled && isUserInitiated) {
        console.warn('Cannot unmute camera: disabled by server');
        return; // Prevent the action
      }
      
      // Check with parent component if action should be allowed
      if (isUserInitiated && onCameraToggle) {
        const shouldAllow = onCameraToggle(enabled);
        if (!shouldAllow) {
          return; // Prevent the action
        }
      }
      
      return isUserInitiated ? saveVideoInputEnabled(enabled) : null;
    },
    [saveVideoInputEnabled, cameraDisabled, onCameraToggle],
  );

  return (
    <div {...htmlProps}>
      {visibleControls.microphone && (
        <div className={`lk-button-group ${microphoneDisabled ? 'lk-microphone-disabled' : ''}`}>
          {/* lk-media-toggle scopes the disabled styling to the toggle itself.
              Without it, `.lk-microphone-disabled .lk-button` also matched the
              sibling device-picker button and its dropdown rows, so a locked mic
              made the input/output list open but be unclickable. */}
          <div
            className="lk-media-toggle"
            style={{
              position: 'relative',
              // opacity: microphoneDisabled ? 0.5 : 1,
              pointerEvents: microphoneDisabled ? 'none' : 'auto',
              cursor: microphoneDisabled ? 'not-allowed' : 'pointer',
            }}
            title={microphoneDisabled ? micLockText : undefined}
            aria-label={microphoneDisabled ? micLockText : undefined}
            aria-describedby={microphoneDisabled ? 'microphone-disabled-tooltip' : undefined}
          >
            <TrackToggle
              source={Track.Source.Microphone}
              showIcon={showIcon}
              onChange={microphoneOnChange}
              onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Microphone, error })}
            >
              {showText && 'Microphone'}
            </TrackToggle>
            {microphoneDisabled && (
              <span
                id="microphone-disabled-tooltip"
                className="lk-microphone-tooltip"
              >
                {micLockText}
              </span>
            )}
          </div>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="audioinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveAudioInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      {visibleControls.camera && (
        <div className={`lk-button-group ${cameraDisabled ? 'lk-camera-disabled' : ''}`}>
          <div
            className="lk-media-toggle"
            style={{
              position: 'relative',
              pointerEvents: cameraDisabled ? 'none' : 'auto',
              cursor: cameraDisabled ? 'not-allowed' : 'pointer',
            }}
            title={cameraDisabled ? cameraLockText : undefined}
            aria-label={cameraDisabled ? cameraLockText : undefined}
            aria-describedby={cameraDisabled ? 'camera-disabled-tooltip' : undefined}
          >
            <TrackToggle
              source={Track.Source.Camera}
              showIcon={showIcon}
              onChange={cameraOnChange}
              onDeviceError={(error) => onDeviceError?.({ source: Track.Source.Camera, error })}
            >
              {showText && 'Camera'}
            </TrackToggle>
            {cameraDisabled && (
              <span
                id="camera-disabled-tooltip"
                className="lk-camera-tooltip"
              >
                {cameraLockText}
              </span>
            )}
          </div>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="videoinput"
              onActiveDeviceChange={(_kind, deviceId) =>
                saveVideoInputDeviceId(deviceId ?? 'default')
              }
            />
          </div>
        </div>
      )}
      {/* Ahead of screen share on purpose. At <=640px the bar is
          `flex-wrap: nowrap; overflow: hidden`, so anything that doesn't fit is
          clipped rather than wrapped — and for a restricted participant, whose
          mic and camera buttons are present but locked, this is the one control
          they actually need. It must not be the one that falls off the end. */}
      {visibleControls.raiseHand && onRaiseHandToggle && (
        <button
          className="lk-button"
          onClick={onRaiseHandToggle}
          title={isHandRaised ? 'Lower your hand' : 'Raise your hand to ask to speak'}
          aria-pressed={isHandRaised}
          data-lk-hand-raised={isHandRaised || undefined}
          style={isHandRaised ? { color: '#fbae16' } : undefined}
        >
          {showIcon && (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 16 16" fill="none">
                <path
                  fill="currentColor"
                  d="M7 1.5a1 1 0 0 1 2 0V7h.5V2.75a1 1 0 0 1 2 0V7h.5V4.5a1 1 0 0 1 2 0v5a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5V6.5a1 1 0 0 1 2 0V9h.5a.5.5 0 0 0 .5-.5V1.5Z"
                />
              </svg>
            </span>
          )}
          {showText && (isHandRaised ? 'Lower hand' : 'Raise hand')}
        </button>
      )}
      {visibleControls.screenShare && browserSupportsScreenSharing && !isMobile && (
        // Wrapped rather than simply hidden when locked: a missing button reads
        // as "this build has no screen sharing", where a locked one with a
        // tooltip says who took it away and implies it can come back.
        <div
          className={`lk-media-toggle ${screenShareDisabled ? 'lk-screenshare-disabled' : ''}`}
          style={{
            position: 'relative',
            pointerEvents: screenShareDisabled ? 'none' : 'auto',
            cursor: screenShareDisabled ? 'not-allowed' : 'pointer',
          }}
          title={screenShareDisabled ? screenShareDisabledReason : undefined}
          aria-label={screenShareDisabled ? screenShareDisabledReason : undefined}
        >
          <TrackToggle
            source={Track.Source.ScreenShare}
            captureOptions={{ audio: true, selfBrowserSurface: 'include' }}
            showIcon={showIcon}
            onChange={onScreenShareChange}
            onDeviceError={(error) => onDeviceError?.({ source: Track.Source.ScreenShare, error })}
          >
            {showText && (isScreenShareEnabled ? 'Stop screen share' : 'Share screen')}
          </TrackToggle>
          {screenShareDisabled && (
            <span className="lk-screenshare-tooltip">{screenShareDisabledReason}</span>
          )}
        </div>
      )}
      {visibleControls.chat && onAuthDrawerToggle && (
        <button
          className="lk-button"
          onClick={onAuthDrawerToggle}
          title="Chat"
          aria-pressed={isAuthDrawerOpen}
        >
          {showIcon && <ChatIcon />}
          {showText && 'Chat'}
        </button>
      )}
      {visibleControls.settings && (
        <SettingsMenuToggle>
          {showIcon && <GearIcon />}
          {showText && 'Settings'}
        </SettingsMenuToggle>
      )}
      {visibleControls.participants && onParticipantsToggle && (
        <button
          className="lk-button"
          onClick={onParticipantsToggle}
          title="Participants"
          aria-pressed={isParticipantsOpen}
        >
          {showIcon && (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={16}
                height={16}
                fill="none"
              >
                <path
                  fill="currentColor"
                  d="M5.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm5 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM0 13.5C0 11.015 2.015 9 4.5 9h2c2.485 0 4.5 2.015 4.5 4.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5Zm11.5-3a.5.5 0 0 0 0 1c1.657 0 3 1.343 3 3a.5.5 0 0 0 .5.5h.5a.5.5 0 0 0 .5-.5c0-2.21-1.79-4-4-4Z"
                />
              </svg>
            </span>
          )}
          {showText && 'Participants'}
          {raisedHandCount > 0 && (
            <span
              title={`${raisedHandCount} ${raisedHandCount === 1 ? 'person has' : 'people have'} raised a hand`}
              style={{ color: '#fbae16', fontWeight: 600 }}
            >
              ✋ {raisedHandCount}
            </span>
          )}
          {waitingCount > 0 && (
            <span
              title={`${waitingCount} ${waitingCount === 1 ? 'person is' : 'people are'} waiting to be let in`}
              style={{ color: '#508afa', fontWeight: 600 }}
            >
              ⏳ {waitingCount}
            </span>
          )}
        </button>
      )}
      {visibleControls.leave && (
        <DisconnectButton>
          {showIcon && <LeaveIcon />}
          {showText && 'Leave'}
        </DisconnectButton>
      )}
      <StartMediaButton />
    </div>
  );
}

